import { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { isArray, orderBy } from 'lodash';
import { Chip, FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useDataService } from '../../../../services/ApiDataService';
import { useTestTypeContext } from './hooks/UseTestTypeContext';
import { useDetailStyles } from '../../../panels/net-test-now-panels/NetTestNowStyles';
import TestnPointInterfaceSection from './sections/TestnPointInterfaceSection';
import ServiceTestAgentsSelectSection from './ServiceTestAgentsSelectSection';
import ServiceTestAgentsTagSection from './ServiceTestAgentsTagSection';
import { QUICK_TEST_TYPE } from './verify/VerifySharedConstants';
import { useServiceTestAgentSelector } from './hooks/UseServiceTestAgentSelector';
import { MULTI } from '../../general/Constants';
import { useTestDetailStyles } from './TestDetailStyles';
import { ALL_MODE, SELECTED_MODE, TAGGED_MODE } from '../ServiceTestsConstants';
import useAgentsContext from './hooks/AgentsContextProvider';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../../stores/AlertsStore';

const AGENT_TAGS_URL = '/ipm/v1/open/agents/tags';

const useLocalStyles = makeStyles((theme) => ({
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));

/**
 * ServiceTestAgentSelector
 *
 * Renders a 3-mode agent picker for service test nPoints configuration.
 * Manages formData.agents = { all, ids, tags, matchAllTags } shape.
 *
 * @param matchingAgentsRef
 */
export function ServiceTestAgentSelector({ matchingAgentsRef }) {
    const { availableAgents, matchingTaggedAgents } = useAgentsContext();

    const styles = useDetailStyles();
    const testDetailStyles = useTestDetailStyles();
    const localClasses = useLocalStyles();
    const { testType } = useTestTypeContext();
    const dataService = useDataService();
    const [, uiAlertActions] = useUiAlerts();

    const {
        mode,
        adapterType,
        selectedAgentObjects,
        tagOp,
        handleModeChange,
        handleTagOpChange,
        handleAdapterTypeChange,
    } = useServiceTestAgentSelector();

    // Lazy-load agent tags: fetch only on first entry into tagged mode, then cache
    const [agentTags, setAgentTags] = useState([]);
    const hasLoadedTagsRef = useRef(false);

    useEffect(() => {
        if (mode !== TAGGED_MODE || hasLoadedTagsRef.current) return;
        hasLoadedTagsRef.current = true;

        dataService
            .get(AGENT_TAGS_URL)
            .then((response) => {
                if (!response?.data || !isArray(response.data)) {
                    setAgentTags([]);
                    return;
                }
                setAgentTags(orderBy(response.data, [(tag) => (tag || '').toLowerCase()], ['asc']));
            })
            .catch(() => {
                uiAlertActions.createAlert(createAlertConfig(WARNING, t`Error to get nPoints tags.`));
            });
    }, [mode, dataService, uiAlertActions]);

    const isMultiAdapter = adapterType === MULTI;

    // Reflect mode-appropriate agents into matchingAgentsRef for Verify feature
    useEffect(() => {
        if (!matchingAgentsRef) return;
        if (mode === TAGGED_MODE) {
            matchingAgentsRef.current = matchingTaggedAgents;
        } else if (mode === SELECTED_MODE) {
            matchingAgentsRef.current = selectedAgentObjects;
        } else {
            matchingAgentsRef.current = availableAgents;
        }
    }, [mode, availableAgents, selectedAgentObjects, matchingTaggedAgents, matchingAgentsRef]);

    const selectedLabel = (
        <span className={localClasses.radioLabel}>
            {t`Run on Select nPoints`}
            {mode === SELECTED_MODE && selectedAgentObjects.length > 0 && (
                <Chip className={testDetailStyles.ngpBadge} size="small" label={selectedAgentObjects.length} />
            )}
        </span>
    );

    const taggedLabel = (
        <span className={localClasses.radioLabel}>
            {t`Run on tagged nPoints`}
            {mode === TAGGED_MODE && matchingTaggedAgents.length > 0 && (
                <Chip className={testDetailStyles.ngpBadge} size="small" label={matchingTaggedAgents.length} />
            )}
        </span>
    );

    return (
        <>
            {isMultiAdapter ? (
                <Typography variant="body2">
                    <Trans>This test will use the nPoints specified in its parent Wi-Fi Group Test</Trans>
                </Typography>
            ) : (
                <>
                    <RadioGroup
                        className={styles.radioWrapper}
                        value={mode}
                        onChange={(e) => handleModeChange(e.target.value)}
                    >
                        <FormControlLabel value={ALL_MODE} label={t`Run on all nPoints`} control={<Radio />} />
                        <FormControlLabel value={SELECTED_MODE} label={selectedLabel} control={<Radio />} />
                        <FormControlLabel value={TAGGED_MODE} label={taggedLabel} control={<Radio />} />
                    </RadioGroup>

                    {mode === SELECTED_MODE && <ServiceTestAgentsSelectSection />}

                    {mode === TAGGED_MODE && (
                        <ServiceTestAgentsTagSection
                            tagOp={tagOp}
                            handleTagOpChange={handleTagOpChange}
                            agentTags={agentTags}
                        />
                    )}
                </>
            )}

            {testType?.name !== QUICK_TEST_TYPE.VOIP_PULSE_TO_PULSE && (
                <TestnPointInterfaceSection
                    adapterType={adapterType}
                    mode={mode}
                    onAdapterTypeChange={handleAdapterTypeChange}
                />
            )}
        </>
    );
}

ServiceTestAgentSelector.propTypes = {
    matchingAgentsRef: PropTypes.shape({ current: PropTypes.any }),
};

ServiceTestAgentSelector.defaultProps = {
    matchingAgentsRef: undefined,
};

export default ServiceTestAgentSelector;
